import { RelatedCharacterTagsQuery } from '@api/character';
import { CharacterOrderType, OutlookPage } from '@api/chatbot';
import { ReadStorySort } from '@api/story';
import {
  CommentFormType,
  CommentObjectType,
  CommentType,
  PAYMENT_VENDOR,
  SettingType,
  VoucherEffectType,
} from '@constants';
import { VendorType } from '@model';

import { FetchPeekRoomsQuery } from './../api/chatbot';

export const QUERY_KEYS = {
  ACHIEVEMENT_PAGE: ['achievmentPage'],
  MESSAGE_TOKEN: ['myMessageToken'],
  SUBSCRIPTIONS: {
    ME: ['subscriptions', 'me'],
    PASS: ['pass'],
  },
  IMAGES: {
    BY_CHAT_ROOM: (chatRoomId: string) => ['images', 'byChatRoom', chatRoomId],
  },
  HIDDEN_FEELING: (chatRoomId?: string) => [
    'hiddenFeeling',
    'byChatRoom',
    chatRoomId,
  ],
  PLOT_RECOMMEND: {
    PROMO: (id: number) => ['plotRecommendPromo', `${id}`],
  },
  PLOT_DEVELOPMENT: {
    PROMO: (id: number) => ['plotDevelopmentPromo', `${id}`],
  },
  HOME_SECTION_LIST: ['homeSectionList'],
  CHATIE_RANKING: {
    CURRENT: ['chatieRanking', 'current'],
    ENTRIES: (runId?: number) => ['chatieRanking', 'entries', `${runId}`],
    STORIES: (storyIds: number[]) => [
      'chatieRanking',
      'stories',
      storyIds.join(','),
    ],
  },
  SECTION: (type: string) => ['section', `${type}`],
  BANNER: (type: string) => ['banner', `${type}`],
  COMMENT: {
    ALL: ['comments'],
    GENERAL: (objectType: CommentObjectType, objectId: number) => [
      'comments',
      objectType,
      objectId,
      CommentType.general,
    ],
    ONE: (commentId: number) => ['comment', commentId],
    LIST: (
      objectType: CommentObjectType,
      objectId: number,
      type = CommentType.general,
      formType = CommentFormType.all
    ) => ['comments', objectType, objectId, type, formType],
    PREVIEW_REPLIES: (commentId: number) => [
      'comments',
      'previewReplies',
      commentId,
    ],
  },
  STORY_CHAT_ROOM: {
    ONE: (id: string | number) => ['story_chat_room', `${id}`],
  },
  FAN: {
    LIST: (userId = 0) => ['fanList', userId],
    INFO: (userId = 0) => ['fanInfo', userId],
    TOP_FAN: (userId = 0) => ['userTopFanList', userId],
  },
  MARKET_ITEM_INVENTORY: {
    LIST: 'userMarketItemInventories',
    ONE: (userId = 0) => [QUERY_KEYS.MARKET_ITEM_INVENTORY.LIST, userId],
  },
  MY_MARKET_ITEM_INVENTORY: {
    LIST: 'myMarketItemInventories',
  },
  POST: {
    LIST: 'myPostList',
    ONE: (id: number) => ['post', id],
    USER_LIST: (userId: number) => ['userPostList', userId],
    CHARACTER_LIST: (characterId: number) => ['characterPostList', characterId],
    CHARACTER_ONE: (postId: number) => ['characterPost', postId],
    COMMUNITY_FEED: (sort: string) => ['communityFeed', sort],
  },
  STORY: {
    LIST: 'stories',
    ONE: (id: number) => ['story', id],
    EPISODE_LIST: (storyId: number, ordering: string) => [
      'storyEpisodeList',
      storyId,
      ordering,
    ],
    PURCHASE: (storyId: number) => ['purchaseStroy', storyId],
    CHATBOT: (id: number, type: string) => ['chatbotInfo', id, type],
    READ_EVENT: (code: string) => ['readEvent', code],
    MY_SUMMARIES: (id: number) => ['myWeeklySummaries', `${id}`],
    SUMMARIES: (id: number) => ['weeklySummaries', `${id}`],
  },
  EPISODE: {
    ONE: (id: number) => ['episode', id],
    CONTENT: (contentKey: string | undefined) => ['episodeContent', contentKey],
    ALL_CONTENT: ['episodeContent'],
  },
  USER: {
    ONE: (id: number | undefined) => ['user', id],
    LIST: (ids: number[]) => ['userList', ...ids],
    SOCIAL_PROVIDER: ['socialProvider'],
    ME: 'me',
    INVITATION_CODE: 'invitationCode',
  },
  SETTING: {
    ONE: (objectType: SettingType, objectId: number) => [
      'setting',
      objectType,
      objectId,
    ],
  },
  READ_STORY: {
    LIST: (sort: ReadStorySort) => ['readStoryList', sort],
    SEARCH_LIST: (q: string) => ['readStoryList', q],
  },
  VOUCHERS: {
    LIST: (type: VoucherEffectType, isAvailable?: boolean) => [
      'vouchers',
      type,
      isAvailable,
    ],
  },
  SECTION_STORY: {
    LIST: (sectionId: number) => ['sectionStoryList', sectionId],
  },
  MY_STORY: {
    STORY: (storyId?: number) => ['myStory', storyId],
    EPISODE_LIST: (storyId: number) => ['myStoryEpisodeList', storyId],
    WAITFREE: (storyId: number) => ['myStoryWaitfree', storyId],
    PUBLISH_CHALLENGE_INFO: (storyId: number) => [
      'publishChallengeInfo',
      storyId,
    ],
  },
  MY_EPISODE: {
    ONE: (id: number) => ['myEpisode', id],
  },
  AD: {
    STORY: (key?: string) => ['storyAd', key],
    STORYFULL: (storyId: number) => ['storyAdFull', storyId],
  },
  LIKED_STORY: {
    LIST: 'likedStoryList',
  },
  FOLLOWING: {
    FEED: 'followingFeed',
    FEED_USER_LIST: 'followingFeedUserList',
  },
  MY_FAN_CLUB: {
    INFO: ['myFanClub'],
  },
  NOTI: {
    INFO: ['userNotificationInfo'],
    CONVERTED: ['converteduserNotificationInfo'],
  },
  EVENT: {
    CHAT_CHARACTER: (storyId: number) => ['character', storyId],
    CHAT_PROFILE: (characterId: number) => ['profile', characterId],
    CHAT_MESSAGE: (roomId: string) => ['chatRoom', roomId],
    CHAT_SITUATION_DETAIL: (situationId: number) => [
      'situationDetail',
      situationId,
    ],
  },
  CERTIFICATION: {
    PHONE: ['certification', 'phone'],
    ADULT: ['certification', 'adult'],
  },
  SITUATION: {
    ONE: (id: number | null) => ['situationDetail', `${id}`],
    GROUP: (id: number) => ['situationGroupDetail', `${id}`],
    LAST_CHAT_ROOM: (id: number) => ['situationLastChatRoom', `${id}`],
    INFO: (characterId: number) => [
      'situationInfoByCharacter',
      `${characterId}`,
    ],
  },
  CHAT_ROOM: {
    PREV: (id: number | string, key?: string) => [
      'prevChatRoom',
      `${id}`,
      `${key}`,
    ],
    DETAIL: (id: number) => ['chatRoomDetail', `${id}`],
    STORY_LIST: ['storyChatList'],
    LIST: (ChatRoomsQueryType?: string) => ['chatRoomList', ChatRoomsQueryType],
    ACTIVE_CHARACTER: (id: number) => ['activeChatRoomsByCharacter', `${id}`],
    ACTIVE_CHARACTER_PAGINATION: (id: number) => [
      'activeChatRoomsPaginationByCharacter',
      `${id}`,
    ],
    RECOMMEND_MESSAGES: (id: number) => ['recommendMessages', `${id}`],
    PRESET: (id: string | undefined | null) => ['preset', `${id}`],
    PRESETS: (key: string | undefined) => ['presets', `${key}`],
    CHEMING_STEP_STATUS: (chatroomId: number | undefined) => [
      'chemingStepStauts',
      `${chatroomId}`,
    ],
  },
  OUT_LOOK: {
    PAGE: (page: OutlookPage) => ['outlookPage', `${page}`],
    COMPONENT: (key: string) => ['outlookComponent', `${key}`],
    COMPONENT_ITEM: (key: string) => ['outlookComponentItem', `${key}`],
    GROUND_FEED: (feedCode: string, query?: string) => [
      'groundFeed',
      `${feedCode}+${query}`,
    ],
    PEEK: (feedCode: string, query?: string) => [
      'outlookPeek',
      `${feedCode}+${query}`,
    ],
    GROUND_FEED_ALL: ['outlookPeek'],
  },
  CHARACTER: {
    FREE: () => ['characterList', 'free'],
    LIKE: (id: number) => ['chatCharacterLike', `${id}`],
    ONE: (id: number | undefined) => ['chatCharacterDetail;', `${id}`],
    LIST: (ids: string[]) => ['characterList', ...ids],
    LIKE_LIST: () => ['characterLikeList'],
    MY_CHARACTER_LIST: (order: CharacterOrderType) => [
      'myCharacterList',
      `${order}`,
    ],
    EDIT: (id: number | undefined) => ['characterEdit', `${id}`],
    USERS: (userId: number, order: CharacterOrderType) => [
      'userChatacterList',
      `${userId}`,
      `${order}`,
    ],
    STORY_CHARACTER: (storyId: number) => ['storyCharacter', `${storyId}`],
    TAG: (tagId: number[], order: string) => [
      'characterListByTag',
      `${tagId}${order}`,
    ],
    RELATED_TAG: (
      tagId: number | string,
      query?: RelatedCharacterTagsQuery
    ) => ['relatedQuery', `${tagId} ${JSON.stringify(query)}`],
    RECOMMAND: (id: string) => ['recommend', `${id}`],
  },
  PLAYER: (id: number | undefined) => ['chatPlayer', `${id}`],
  MY_STATS: ['chatPlayer', `myStats`],
  DRIVE: {
    LIST: (type: string) => ['driveList', `${type}`],
    FILES: (id: number | undefined) => ['driveFiles', `${id}`],
  },
  PAYMENT: {
    RECOMMEND: (vendor: PAYMENT_VENDOR) => ['payment-recommend', `${vendor}`],
    ITEMS: (vendor: VendorType) => ['payment', `${vendor}`],
    BUNDLE_LIST: (vendor: VendorType) => ['bundleList', `${vendor}`],
    BUNDLE_PASS: (id: number) => ['bundlePass', `${id}`],
    MY_BUNDLE_LIST: () => ['myBundleList'],
  },
  PEEK: {
    detail: (id: number | string) => ['peekDetail', `${id}`],
    messages: (id: number | string) => ['peekMessages', `${id}`],
    list: (query: FetchPeekRoomsQuery) => [
      'peekRoomList',
      `${JSON.stringify(query)}`,
    ],
    OUTLOOK_CODE: (feedCode: string, query?: string) => [
      'outlookPeek',
      `${feedCode}+${query}`,
    ],
  },
  PROMOTION: {
    ONE: (id: number | string) => ['promotion', `${id}`],
  },
  CHAPTER: {
    ONE: (chatRoomId: number, chapterId: number) => [
      'chapter',
      `${chatRoomId}`,
      `${chapterId}`,
    ],
  },
  VIRAL_CONTENT: {
    ONE: (uuid: string) => ['viral_content', uuid],
  },
};

export const CHEMING_QUERY_KEYS = {
  CHEMI: {
    THEME: ['chemiTheme'],
    STORY: (id: number) => ['chemiStory', id],
  },
  TAG_COLLECTION: {
    LIST: (name: string) => ['tagCollection', name],
  },
};
